import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { Plant, Pot, PotSize } from "@/types";
import { POT_SIZE_ORDER, formatApproxVolumeLiters } from "@/app/lib/potSizes";
import { Button } from "@/app/components/system/Button";
import { PlantMark } from "@/app/components/PlantMark";
import { PotIllustration } from "@/app/components/PotIllustration";
import { DeleteConfirmBanner } from "@/app/components/DeleteConfirmBanner";
import { EditScreenHeader } from "@/app/components/EditScreenHeader";
import { SunHoursPicker } from "@/app/components/SunHoursPicker";
import { isSunRangeInvalid } from "@/app/lib/sunHours";
import { usePots } from "../lib/data-context";

type PotDraft = { name: string; shape: string; size?: PotSize };

const SHAPES = ["terracotta", "basket", "concrete", "long"] as const;

export function PotEditScreen() {
  const navigate = useNavigate();
  const { id: potId } = useParams<{ id: string }>();
  const isNew = potId === undefined;
  const { pots, addPot, editPot, deletePlant, deletePot } = usePots();
  const pot: Pot | PotDraft | undefined = isNew
    ? { name: "", shape: "terracotta", size: "medium" as const }
    : pots.find((x) => x.id === potId);
  const plantsInPot: Plant[] = !isNew && pot ? (pot as Pot).plants : [];
  const isExistingPot = pot != null && "id" in pot;
  const existing = isExistingPot ? (pot as Pot) : null;
  const [name, setName] = useState(pot?.name ?? "");
  const [shape, setShape] = useState(pot?.shape ?? "terracotta");
  const [size, setSize] = useState<PotSize>(pot && "size" in pot && pot.size ? pot.size : "medium");
  const [sunStart, setSunStart] = useState<string>(existing?.sunHours?.start ?? "");
  const [sunEnd, setSunEnd] = useState<string>(existing?.sunHours?.end ?? "");
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  if (!pot) return null;

  const sunStartVal = sunStart || null;
  const sunEndVal = sunEnd || null;
  const sunInvalid = isSunRangeInvalid(sunStartVal, sunEndVal);

  const sunChanged = !isNew && existing && (
    (sunStartVal ?? null) !== (existing.sunHours?.start ?? null)
    || (sunEndVal ?? null) !== (existing.sunHours?.end ?? null)
  );

  const sizeChanged = !isNew && existing && size !== existing.size;

  const dirty = isNew
    ? name.trim().length > 0
    : (name.trim() !== pot.name || shape !== pot.shape || sizeChanged || sunChanged);

  const save = async () => {
    if (sunInvalid) return;
    if (isNew) {
      const created = await addPot({ name: name.trim(), shape, size, sunStart: sunStartVal, sunEnd: sunEndVal });
      if (created) navigate(`/pots/${created.id}`);
    } else if ("id" in pot) {
      await editPot(pot.id, {
        name: name.trim() || pot.name,
        shape,
        size,
        sunStart: sunStartVal,
        sunEnd: sunEndVal,
      });
      navigate(`/pots/${pot.id}`);
    }
  };

  const cancel = () => {
    if (isNew) navigate("/add");
    else if ("id" in pot) navigate(`/pots/${pot.id}`);
  };

  const canDelete = plantsInPot.length === 0;

  return (
    <div className="bg-cream min-h-full pb-10">
      <EditScreenHeader
        title={isNew ? "New pot" : "Edit pot"}
        saveLabel={isNew ? "Create" : "Save"}
        saveDisabled={!dirty || sunInvalid}
        onCancel={cancel}
        onSave={save}
      />

      <div className="grid place-items-center pt-2 pb-6 bg-cream-2 mx-4.5 mb-4 rounded-[18px] border border-line">
        <PotIllustration shape={shape} plants={plantsInPot} size="large" />
      </div>

      <div className="px-4.5 flex flex-col gap-4">
        <section className="flex flex-col gap-1">
          <h3 className="form-h mb-0">Name</h3>
          <input
            className="field-input-bare"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Kitchen herb trio"
          />
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="form-h mb-0">Shape</h3>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden pb-0.5">
            {SHAPES.map((s) => {
              const on = shape === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setShape(s)}
                  className={`bg-paper border-[1.5px] rounded-[10px] pt-1.5 pb-2 px-1 cursor-pointer flex flex-col items-center gap-0.5 min-w-18 flex-none ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                >
                  <PotIllustration shape={s} plants={[]} />
                  <span className={`text-[10px] capitalize ${on ? "text-moss" : "text-ink-3"}`}>{s}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-1">
          <div>
            <h3 className="form-h mb-0">Pot size</h3>
            <p className="font-sans text-xs text-ink-3 m-0 mt-1 leading-snug">
              Approximate soil volume for watering context.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-1.5 w-full">
            {POT_SIZE_ORDER.map((sz) => {
              const on = size === sz;
              return (
                <button
                  type="button"
                  key={sz}
                  onClick={() => setSize(sz)}
                  className={`bg-paper border-[1.5px] rounded-[10px] py-2 px-1.5 cursor-pointer flex flex-col items-center justify-center gap-0.5 min-w-0 w-full ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                >
                  <span className={`text-[11px] font-medium capitalize ${on ? "text-moss" : "text-ink"}`}>{sz}</span>
                  <span className={`text-[10px] ${on ? "text-moss" : "text-ink-3"}`}>
                    {formatApproxVolumeLiters(sz)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-1">
          <SunHoursPicker
            start={sunStart}
            end={sunEnd}
            onStartChange={setSunStart}
            onEndChange={setSunEnd}
            headingStyle="section"
          />
        </section>

        {!isNew && "id" in pot && (
          <>
            <section className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <h3 className="form-h mb-0 shrink-0">Plants in this pot</h3>
                <span className="font-sans text-xs text-ink-3">
                  {plantsInPot.length} {plantsInPot.length === 1 ? "plant" : "plants"}
                </span>
              </div>
              {plantsInPot.length === 0 ? (
                <p className="font-sans text-[13px] text-ink-3 m-0 italic">No plants in this pot.</p>
              ) : (
                <ul className="list-none m-0 p-0 flex flex-col gap-2">
                  {plantsInPot.map((p) => (
                    <li key={p.id} className="grid items-center gap-3 px-3 py-2.5 bg-paper border border-line rounded-xl grid-cols-[auto_1fr_auto]">
                      <PlantMark plant={p} size={36} />
                      <div className="min-w-0">
                        <h4 className="serif m-0 text-[15px] text-ink">{p.nickname}</h4>
                        <p className="muted-sm mt-px mb-0 mx-0 text-xs text-ink-3">{p.species?.common ?? p.species?.scientific ?? ""}</p>
                      </div>
                      {confirmRemoveId === p.id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12.5px] text-ink-2 mr-0.5">Delete?</span>
                          <Button variant="secondary" onClick={() => setConfirmRemoveId(null)}>Keep</Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={async () => { await deletePlant(p.id); setConfirmRemoveId(null); }}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemoveId(p.id)}
                          className="bg-transparent border-0 text-ink-3 font-sans text-[13px] font-medium cursor-pointer px-2.5 py-1.5 rounded-lg hover:text-[oklch(0.5_0.14_28)] hover:bg-[oklch(0.95_0.04_28/0.5)]"
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p className="font-sans text-xs text-ink-3 m-0 leading-snug">
                Removing a plant deletes it from your garden. This can&apos;t be undone.
              </p>
            </section>

            <DeleteConfirmBanner
              triggerLabel="Delete pot"
              question={`Delete "${pot.name}"? This can't be undone.`}
              disabled={!canDelete}
              helpText="Remove all plants before deleting."
              onConfirm={async () => {
                await deletePot(pot.id);
                navigate("/");
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
